"use client";

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from "next-intl";
import { cn } from "@repo/ui/utils";
import { DialogCard } from "@/components/cards/dialog-card";
import { VisualWrapper } from "@/components/common/visual/visual.component";
import { PersonCardPanel } from "./person-card-panel.component";
import type { PersonCardData } from "./image-card-grid.types";
interface PersonCardProps {
  /** Portrait image rendered on the card face. */
  visual: PersonCardData["visual"];
  /** Full name shown beneath the image. */
  personName: PersonCardData["personName"];
  /** Optional role / job title shown beneath the name. */
  role: PersonCardData["role"];
  /**
   * Biography rendered inside the slide-out panel. When null (and no
   * `linkedinUrl`) the card renders as a non-interactive `<article>`.
   */
  bio: PersonCardData["bio"];
  /** LinkedIn URL rendered as a link-button in the panel. */
  linkedinUrl: PersonCardData["linkedinUrl"];
  /** Horizontal layout at mobile (81px image left, text right), vertical at desktop. */
  mobileHorizontal?: boolean;
  /** Additional CSS class names merged onto the card root. */
  className?: string;
}
export function PersonCard(t0) {
  const $ = _c(33);
  const {
    visual,
    personName,
    role,
    bio,
    linkedinUrl,
    mobileHorizontal: t1,
    className
  } = t0;
  const mobileHorizontal = t1 === undefined ? false : t1;
  const t = useTranslations("personCard");
  const hasPanelContent = Boolean(bio) || Boolean(linkedinUrl);
  const flexClass = mobileHorizontal ? "flex-row items-start desktop:flex-col desktop:items-stretch" : "flex-col";
  let t2;
  if ($[0] !== mobileHorizontal || $[1] !== visual) {
    t2 = visual && <div className={cn("overflow-hidden rounded-sm desktop:rounded-md", mobileHorizontal ? "w-[81px] shrink-0 aspect-[3/4] desktop:w-full desktop:shrink" : "aspect-[3/4] w-full")}><VisualWrapper visual={visual} className="size-full" visualClassName="size-full" imgClassName="object-cover" extraImageProps={{
        sizes: mobileHorizontal ? "(max-width: 1024px) 81px, 25vw" : "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw",
        cropAspect: 0.75
      }} /></div>;
    $[0] = mobileHorizontal;
    $[1] = visual;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = mobileHorizontal && "min-w-0";
  let t4;
  if ($[3] !== t3) {
    t4 = cn("flex w-full flex-col gap-2", t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== personName) {
    t5 = <h3 className="typography-mobile-heading-subtitle-1 desktop:typography-desktop-heading-subtitle-1 text-text-primary">{personName}</h3>;
    $[5] = personName;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== role) {
    t6 = role && <p className="typography-body-sm-regular text-text-secondary">{role}</p>;
    $[7] = role;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = <div className={t4}>{t5}{t6}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t2 || $[14] !== t7) {
    t8 = <>{t2}{t7}</>;
    $[13] = t2;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const cardBody = t8;
  let t9;
  if ($[16] !== cardBody || $[17] !== className || $[18] !== flexClass || $[19] !== hasPanelContent || $[20] !== personName || $[21] !== t) {
    t9 = hasPanelContent ? <button type="button" className={cn("group flex w-full cursor-pointer gap-4 text-left", flexClass, "rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-active", className)} data-testid="person-card" aria-label={t("openBiography", {
      name: personName
    })}>{cardBody}</button> : <article className={cn("flex gap-4", flexClass, className)} data-testid="person-card">{cardBody}</article>;
    $[16] = cardBody;
    $[17] = className;
    $[18] = flexClass;
    $[19] = hasPanelContent;
    $[20] = personName;
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const trigger = t9;
  let t10;
  if ($[23] !== bio || $[24] !== linkedinUrl || $[25] !== personName || $[26] !== role || $[27] !== visual) {
    t10 = <PersonCardPanel visual={visual} personName={personName} role={role} bio={bio} linkedinUrl={linkedinUrl} />;
    $[23] = bio;
    $[24] = linkedinUrl;
    $[25] = personName;
    $[26] = role;
    $[27] = visual;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== hasPanelContent || $[30] !== t10 || $[31] !== trigger) {
    t11 = <DialogCard enabled={hasPanelContent} trigger={trigger} panel={t10} contentClassName="desktop:max-w-[803px]" />;
    $[29] = hasPanelContent;
    $[30] = t10;
    $[31] = trigger;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
}